import React, { type FC, use, useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, Avatar, Button, Description, FieldError, InputGroup, Spinner, TextField, toast } from "@heroui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Envelope, Eye, EyeSlash, Globe } from "@gravity-ui/icons";

import { AuthErrorCodes, GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";

import { AppRoutes } from "@domain/routing/routeConstants";
import { auth } from "@domain/api/firebase/firebase";
import { UserContext } from "@domain/context/UserProvider";
import { logger } from "@domain/infrastructure/logger";
import { useValidationReactHookForm } from "@domain/infrastructure/helpers/validation";
import datevLogo from "../../../assets/datev.png";
import elsterLogo from "../../../assets/elster.png";
import { getServerlessApi } from "@domain/api/backendApi";

export interface LoginFormValues {
    email: string;
    password: string;
}

interface IProps {
    show2fa: (values: LoginFormValues) => void;
}

export const LoginForm: FC<IProps> = ({ show2fa }) => {
    const intl = useIntl();
    const v = useValidationReactHookForm();
    const [showError, setShowError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { isLoading: isUserLoading } = use(UserContext);
    const [query] = useSearchParams();
    const redirectRef = useRef(query.get("redirect"));
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormValues>({ mode: "all" });

    const handleGoogle = useCallback(() => {
        const provider = new GoogleAuthProvider();
        signInWithPopup(auth, provider).then((result) => {
            // This gives you a Google Access Token. You can use it to access the Google API.
            const credential = GoogleAuthProvider.credentialFromResult(result);
            const token = credential.accessToken;
            // The signed-in user info.
            const user = result.user;
        });
    }, []);

    const toggleShowPassword = useCallback(() => setShowPassword((prev) => !prev), []);

    const onSubmit = useCallback(
        async (values: LoginFormValues) => {
            setLoading(true);
            const { host, headers } = await getServerlessApi();

            try {
                const res = await fetch(`${host}/auth/v1/authorize`, {
                    method: "POST",
                    body: JSON.stringify({ data: values }),
                    headers: { ...headers, "Content-Type": "application/json" },
                });
                const data = await res.json();
                if (!res.ok) {
                    throw new Error(data?.error?.message ?? res.statusText);
                }
                if (data?.use2fa) {
                    show2fa(values);
                } else {
                    await signInWithEmailAndPassword(auth, values.email, values.password);
                    const pushRedirectOrHome = redirectRef.current || AppRoutes.home;
                    setTimeout(() => navigate(pushRedirectOrHome), 250);
                }
            } catch (e) {
                if (e.message === AuthErrorCodes.INVALID_EMAIL) {
                    setShowError(true);
                } else {
                    logger.crit(e, { values });
                    toast.danger(intl.formatMessage({ id: "app.global.error" }));
                }
            } finally {
                setLoading(false);
            }
        },
        [intl, navigate, show2fa],
    );

    const { ref: emailRef, ...emailReg } = register("email", { required: v.required, validate: v.email });
    const { ref: passwordRef, ...passwordReg } = register("password", { required: v.required });

    return (
        <>
            {showError && (
                <Alert status="danger" className="mb-4">
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>{intl.formatMessage({ id: "app.login.error.fail" })}</Alert.Title>
                    </Alert.Content>
                </Alert>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <TextField
                    aria-label="E-Mail"
                    isInvalid={!!errors.email}
                    {...emailReg}
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- closes over per-render RHF register onChange identity; wrapping cannot stabilize it and would risk the controlled-field sync
                    onChange={(val: string) => {
                        void emailReg.onChange({ target: { value: val } });
                        setShowError(false);
                    }}
                >
                    <InputGroup>
                        <InputGroup.Input
                            ref={emailRef}
                            placeholder={intl.formatMessage({ id: "app.global.email" })}
                            data-testid="login-input-email"
                        />
                        <InputGroup.Suffix>
                            <Button isIconOnly isDisabled size="sm" variant="ghost">
                                <Envelope className="text-muted size-4" />
                            </Button>
                        </InputGroup.Suffix>
                    </InputGroup>
                    <FieldError>{errors.email?.message}</FieldError>
                </TextField>
                <TextField
                    aria-label="Passwort"
                    isInvalid={!!errors.password}
                    {...passwordReg}
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- closes over per-render RHF register onChange identity; wrapping cannot stabilize it and would risk the controlled-field sync
                    onChange={(val: string) => {
                        void passwordReg.onChange({ target: { value: val } });
                        setShowError(false);
                    }}
                >
                    <InputGroup>
                        <InputGroup.Input
                            ref={passwordRef}
                            type={showPassword ? "text" : "password"}
                            placeholder={intl.formatMessage({ id: "app.global.password" })}
                            data-testid="login-input-password"
                        />
                        <InputGroup.Suffix>
                            <Button isIconOnly size="sm" variant="ghost" onPress={toggleShowPassword}>
                                {showPassword ? <EyeSlash className="size-4" /> : <Eye className="size-4" />}
                            </Button>
                        </InputGroup.Suffix>
                    </InputGroup>
                    <FieldError>{errors.password?.message}</FieldError>
                    <Description>
                        <Link to={AppRoutes.authPasswordReset}>
                            <FormattedMessage id="app.reset_password.caption" />
                        </Link>
                    </Description>
                </TextField>
                <Button
                    type="submit"
                    size="lg"
                    isDisabled={loading || isUserLoading}
                    isPending={loading || isUserLoading}
                    data-testid="login-btn-submit"
                    fullWidth
                >
                    {({ isPending }) => (
                        <>
                            {isPending ? <Spinner color="current" size="sm" /> : null}
                            <FormattedMessage id="app.button.login" tagName="span" />
                        </>
                    )}
                </Button>
            </form>
            <div className="flex gap-4 justify-center m-2">
                <FormattedMessage id="app.global.do_not_have_account" />
                &nbsp;
                <Link to={AppRoutes.authRegister} className="underline">
                    <FormattedMessage id="app.global.register_now" />
                </Link>
            </div>

            <hr />
            <div className="flex gap-4 justify-center mt-4">
                <Button variant="ghost" onPress={handleGoogle}>
                    <Globe className="size-4" />
                    Google
                </Button>
            </div>
            <div className="fixed bottom-[-150px] left-1/2 -translate-x-1/2">
                <div className="flex items-start gap-12">
                    <div className="flex flex-col items-center">
                        <Avatar className="w-[100px] h-[100px]">
                            <Avatar.Image src={elsterLogo} alt="ELSTER UStVA" />
                        </Avatar>
                        {/* <span style={{ textAlign: "center", color: "white" }}>ELSTER UStVA</span> */}
                    </div>
                    <div className="flex flex-col items-center">
                        <Link to="https://www.datev.de/web/de/marktplatz/datev-schnittstellen-anbieter/#anchor_st_fold_out_section__datev_68">
                            <Avatar className="w-[100px] h-[100px]">
                                <Avatar.Image src={datevLogo} alt="DATEV Buchungsdatenservice" />
                            </Avatar>
                        </Link>
                        {/* <span style={{ textAlign: "center", color: "white" }}>DATEV Buchungsdatenservice</span> */}
                    </div>
                </div>
            </div>
        </>
    );
};
